import { useState, useEffect } from 'react'

import { useForm, useWatch } from 'react-hook-form'

import useUseCaseTranslate from 'Pods/LazyLoaded/UseCaseAreaSidebar/hooks/useUseCaseTranslate'

import { TECHNOLOGY_OPTIONS, MATERIAL_OPTIONS, PRINT_RESULT_OPTIONS, PRINT_TIME_OPTIONS } from '../../../Utils/Data'

// Normalize usedSupport to boolean (handles "yes"/"no" strings and true/false)
const normalizeUsedSupport = (value) => {
  if (value === 'yes' || value === true) return true
  if (value === 'no' || value === false) return false
  return false
}

function useUseCaseEdit({
  useCase,
  printerTypeOptions,
  onEditUseCase,
  isUpdating,
  onShowNotification,
  onCloseGallery,
  onEditingChange,
}) {
  const { t, tv } = useUseCaseTranslate()
  const [isEditing, setIsEditing] = useState(false)
  const [isFormReady, setIsFormReady] = useState(false)
  const [mediaChanges, setMediaChanges] = useState({
    added: [],      // New files/embeds to add
    removed: [],     // Existing embed IDs to remove
  })
  const [initialFormValues, setInitialFormValues] = useState(null)
  const { control, reset } = useForm({
    defaultValues: {
      technology: null,
      printerType: null,
      material: null,
      printSuccess: null,
      description: '',
      printTime: null,
      usedSupport: false,
    },
  })

  // Watch all form values reactively for change detection
  const watchedValues = useWatch({ control })

  // Populate form when entering edit mode
  useEffect(() => {
    if (!isEditing) {
      setIsFormReady(false)
      return
    }

    // Find matching options for select fields
    const findOption = (options, group, value) => {
      const option = options.find(opt => opt.value === value)
      return option ? { ...option, label: t(`options.${group}.${option.value}`) } : null
    }
    const technologyOption = findOption(TECHNOLOGY_OPTIONS, 'technology', useCase.technology)
    const materialOption = findOption(MATERIAL_OPTIONS, 'material', useCase.material)
    const printSuccessOption = findOption(PRINT_RESULT_OPTIONS, 'printResult', useCase.printSuccess)
    const printTimeOption = findOption(PRINT_TIME_OPTIONS, 'printTime', useCase.printTime)

    const printerTypeId = useCase.printerType?.id
    const printerTypeName = useCase.printerType?.name
    const printerOption = (printerTypeId && printerTypeName)
      ? printerTypeOptions.find(opt => opt.printerTypeId === printerTypeId)
      : null

    // Reset form with all current values
    const initialValues = {
      technology: technologyOption || null,
      printerType: printerOption || null,
      material: materialOption || null,
      printSuccess: printSuccessOption || null,
      description: useCase.description || '',
      printTime: printTimeOption || null,
      usedSupport: normalizeUsedSupport(useCase.usedSupport),
    }

    reset(initialValues)
    setInitialFormValues(initialValues)
    setIsFormReady(true)
  }, [isEditing, useCase, printerTypeOptions, reset, t])

  const hasMediaChanges = mediaChanges.added.length > 0 || mediaChanges.removed.length > 0

  // Calculate how many embeds would remain after applying changes
  const resultingEmbedCount =
    useCase.useCaseEmbeds.length - mediaChanges.removed.length + mediaChanges.added.length
  const hasValidEmbedCount = resultingEmbedCount > 0

  const hasRealChanges = isFormReady && initialFormValues && (
    hasMediaChanges ||
    watchedValues.technology?.value !== initialFormValues.technology?.value ||
    watchedValues.printerType?.printerTypeId !== initialFormValues.printerType?.printerTypeId ||
    watchedValues.material?.value !== initialFormValues.material?.value ||
    watchedValues.printSuccess?.value !== initialFormValues.printSuccess?.value ||
    (watchedValues.description || '') !== (initialFormValues.description || '') ||
    watchedValues.printTime?.value !== initialFormValues.printTime?.value ||
    Boolean(watchedValues.usedSupport) !== Boolean(initialFormValues.usedSupport)
  )

  // Can only save if there are changes AND at least one embed would remain
  const canSave = hasRealChanges && hasValidEmbedCount

  const handleEditClick = (event) => {
    event.stopPropagation()
    setIsEditing(true)
    onEditingChange?.(useCase.id)
  }

  const handleAddMedia = (media) => {
    setMediaChanges(prev => ({
      ...prev,
      added: [...prev.added, media],
    }))
  }

  const handleRemoveExistingMedia = (embedId) => {
    setMediaChanges(prev => {
      const isNewEmbed = prev.added.some(media => media.id === embedId)

      if (isNewEmbed) {
        return {
          ...prev,
          added: prev.added.filter(media => media.id !== embedId),
        }
      }

      return {
        ...prev,
        removed: [...prev.removed, embedId],
      }
    })
  }

  const handleRemoveNewMedia = (index) => {
    setMediaChanges(prev => ({
      ...prev,
      added: prev.added.filter((_, idx) => idx !== index),
    }))
  }

  const handleReorderMedia = (reorderedMedia) => {
    setMediaChanges(prev => ({
      ...prev,
      added: reorderedMedia,
    }))
  }

  const handleSaveClick = (event) => {
    event.stopPropagation()

    if (isUpdating || !canSave) {
      if (!hasRealChanges) {
        setIsEditing(false)
      }
      return
    }

    if (onCloseGallery) {
      onCloseGallery()
    }

    if (onShowNotification) {
      onShowNotification(
        tv('status.updating'),
        'info'
      )
    }

    // Capture current media changes before clearing to avoid duplicate keys
    // during optimistic update (useCase.useCaseEmbeds will include these items)
    const currentMediaChanges = { ...mediaChanges }
    setMediaChanges({ added: [], removed: [] })

    onEditUseCase(
      useCase.id,
      watchedValues.technology?.value || null,
      watchedValues.printerType?.printerTypeId || null,
      watchedValues.printerType?.printerTypeName || null,
      watchedValues.material?.value || null,
      watchedValues.printSuccess?.value || null,
      watchedValues.description || null,
      watchedValues.printTime?.value || null,
      Boolean(watchedValues.usedSupport),
      () => {
        if (!onCloseGallery) {
          setIsEditing(false)
        }
        onEditingChange?.(null)
      },
      () => {
        // Restore media changes on error so user can retry
        setMediaChanges(currentMediaChanges)
      },
      currentMediaChanges
    )
  }

  const handleCancelClick = (event) => {
    event.stopPropagation()
    setIsEditing(false)
    setInitialFormValues(null)
    reset()
    // Reset media changes on cancel
    setMediaChanges({ added: [], removed: [] })
    onEditingChange?.(null)
  }

  return {
    isEditing,
    isFormReady,
    control,
    mediaChanges,
    hasRealChanges,
    canSave,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleAddMedia,
    handleRemoveExistingMedia,
    handleRemoveNewMedia,
    handleReorderMedia,
  }
}

export default useUseCaseEdit
